import { useEffect, useState } from "react";
import { Input as NextUiInput } from "@nextui-org/react";

import HideIcon from "../../assets/images/icons/hidden-icon.svg";
import ShowIcon from "../../assets/images/icons/visibility-icon.svg";
import InvalidIcon from "../../assets/images/icons/ui/states/alert-icon.svg";
import SearchIcon from "../../assets/images/icons/search-icon.svg";

import styles from "./Input.module.scss";
import { getValue, removeMask } from "./utils/index";

import InfoMessage from "../InfoMessage/InfoMessage";

import { InputProps as NextUiInputProps } from "@nextui-org/react";
import { ReactNode } from "react";
import useScreenSize from "../../hooks/useScreenSize";

export type InputType =
  | "text"
  | "number"
  | "search"
  | "password"
  | "money"
  | "cuit"
  | "dni";

export interface InputProps extends NextUiInputProps {
  balance?: number;
  balanceCurrency?: string;
  currency?: string;
  customInfo?: ReactNode;
  errorMessage?: ReactNode;
  infoMessage?: ReactNode;
  initialValue?: string;
  isFormField?: boolean;
  isNumberPercentage?: boolean;
  maxLength?: number;
  info?: "BALANCE" | "CUSTOM";
  tooltip?: ReactNode;
  touched?: boolean;
  type?: InputType;
  hasLabel?: boolean;
  /** Show start content even when there's no value set. This only applies to `type: money` inputs. */
  alwaysShowStartContent?: boolean;
}

const Input = (props: InputProps) => {
  const {
    initialValue,
    maxLength,
    infoMessage,
    errorMessage,
    balance,
    balanceCurrency = "ARS",
    isInvalid,
    isFormField,
    isNumberPercentage,
    label,
    endContent,
    startContent,
    type = "text",
    description,
    currency = "ARS",
    info,
    customInfo,
    hasLabel = true,
    className,
    variant = "bordered",
    alwaysShowStartContent,
  } = props;

  const {
    container,
    base,
    input,
    inputWrapper,
    innerWrapper,
    label: labelStyle,
    helperWrapper,
    errorMessage: errorMessageStyle,
    description: descriptionStyle,
    startContent: startContentStyle,
    endContent: endContentStyle,
    error,
    icon,
    touched: touchedStyle,
    validated,
    balance: balanceStyle,
    number,
    formField,
    info: infoStyle,
    noLabel,
    underlinedVariant,
  } = styles;

  const [isVisible, setIsVisible] = useState(false);
  const [value, setValue] = useState(initialValue || props.value || "");
  const [touched, setTouched] = useState(false);

  const { isMobile } = useScreenSize();

  useEffect(() => {
    if (typeof props.value === "string") {
      setValue(props.value as string);
    }
  }, [props.value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const maskedValue = e.target.value;

    const unmaskedValue = removeMask(maskedValue, type);

    if (unmaskedValue.length <= (maxLength ?? Infinity)) {
      // Save unmasked value to state
      setValue(unmaskedValue);

      const event = { ...e };
      // Send unmasked value to event in onChange
      event.target.value = unmaskedValue;

      if (props.onChange) props.onChange(event);
    }
  };

  const getCurrencySymbol = (locale: string, currency: string) => {
    const symbol =
      currency === "USD"
        ? "U$S"
        : (0)
            .toLocaleString(locale, {
              style: "currency",
              currency,
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            })
            .replace(/\d/g, "")
            .trim();

    return (
      <div className="pointer-events-none flex items-center">{symbol}</div>
    );
  };

  const getErrorMessage = () => (
    <div className={error}>
      <img src={InvalidIcon} className={icon} />
      {errorMessage ?? "Valor inválido"}
    </div>
  );

  const getEyeButton = () => (
    <button
      className="focus:outline-none"
      type="button"
      onClick={() => {
        setIsVisible(!isVisible);
      }}
    >
      {isVisible ? <img src={ShowIcon} /> : <img src={HideIcon} />}
    </button>
  );

  const getBalanceLabel = () => (
    <span className={balanceStyle}>
      Saldo disponible{" "}
      <span className={number}>
        {(balance ?? 0).toLocaleString("es-AR", {
          style: "currency",
          currency: balanceCurrency,
        })}
      </span>
    </span>
  );

  useEffect(() => {
    if (initialValue) setValue(initialValue);
  }, [initialValue]);

  return (
    <>
      <NextUiInput
        radius="sm"
        disableAutosize
        {...props}
        variant={variant}
        onKeyDown={(e) => {
          if (props.onKeyDown) props.onKeyDown(e);
          if (type === "number" && ["ArrowUp", "ArrowDown"].includes(e.key))
            e.preventDefault();
        }}
        onWheel={(e) => {
          const target = e.target as HTMLInputElement;
          target.blur();
        }}
        onChange={handleChange}
        onBlur={(e) => {
          setTouched(true);
          if (props.onBlur) props.onBlur(e);
        }}
        className={`${className ?? ""} ${container}`}
        classNames={{
          ...props.classNames,
          base: `${base} ${!hasLabel && noLabel} ${
            variant === "underlined" ? underlinedVariant : ""
          } ${props.classNames?.base}`,
          input: `${input} ${props.classNames?.input}`,
          inputWrapper: `${inputWrapper} ${
            variant === "underlined"
              ? `${underlinedVariant} after:bg-danger-500`
              : ""
          }  ${isInvalid && validated} ${
            (props.touched || touched) && touchedStyle
          } ${props.classNames?.inputWrapper}`,
          innerWrapper: `${innerWrapper} ${props.classNames?.innerWrapper} group-data-[has-label=true]:items-center`,
          label: `${labelStyle} ${isFormField && formField} ${
            (props.touched || touched) && touchedStyle
          } ${props.classNames?.label}`,
          helperWrapper: `${helperWrapper} ${props.classNames?.helperWrapper}`,
          errorMessage: `${errorMessageStyle} ${props.classNames?.errorMessage}`,
          description: `${descriptionStyle} ${props.classNames?.description}`,
        }}
        type={
          (props.type === "password" && isVisible) ||
          props.type === "search" ||
          props.type === "money" ||
          props.type === "cuit" ||
          props.type === "dni"
            ? "text"
            : props.type
        }
        errorMessage={
          isInvalid && (props.touched || touched) && <>{getErrorMessage()}</>
        }
        description={!isInvalid && description}
        // Mask the value to show
        value={
          ["money", "cuit", "dni"].includes(type)
            ? getValue(type, value)
            : value
        }
        startContent={
          startContent ||
          (["search", "money"].includes(type) && (
            <span className={startContentStyle}>
              {startContent}
              {type === "search" && <img src={SearchIcon} className={icon} />}
              {type === "money" &&
                (props.value || value || alwaysShowStartContent) &&
                getCurrencySymbol("es-AR", currency)}
            </span>
          ))
        }
        endContent={
          <span className={endContentStyle}>
            <>
              {endContent}
              {type === "number" && isNumberPercentage && "%"}
              {type === "password" && getEyeButton()}
            </>
          </span>
        }
        label={
          label && (
            <>
              <span
                style={
                  isMobile
                    ? {
                        fontSize: "0.7rem",
                      }
                    : {}
                }
              >
                {label}
              </span>
              <span className={infoStyle}>
                {info === "BALANCE" && balance ? (
                  getBalanceLabel()
                ) : info === "CUSTOM" && !!customInfo ? (
                  customInfo
                ) : (
                  <></>
                )}
              </span>
            </>
          )
        }
      />
      {infoMessage && !(isInvalid && (props.touched || touched)) && (
        <InfoMessage
          message={infoMessage}
          className={`${helperWrapper} flex gap-2 items-center`}
        />
      )}
    </>
  );
};

export default Input;
